import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/profile-components/Navbar";
import Topbar from "../../components/profile-components/Topbar";
import API_BASE_URL from "../../config";
import "../../pages/SalesPages.css";

function SubscriptionPlanEditor() {
  const navigate = useNavigate();
  const { id } = useParams();

  const isNew = id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [planKey, setPlanKey] = useState("");
  const [planName, setPlanName] = useState("");
  const [tierRank, setTierRank] = useState("");
  const [description, setDescription] = useState("");
  const [pricingModel, setPricingModel] = useState("flat");
  const [minUsers, setMinUsers] = useState(1);
  const [maxUsers, setMaxUsers] = useState("");
  const [monthlyPrice, setMonthlyPrice] = useState("");
  const [additionalUserMonthlyPrice, setAdditionalUserMonthlyPrice] = useState("");
  const [annualPrice, setAnnualPrice] = useState("");
  const [additionalUserAnnualPrice, setAdditionalUserAnnualPrice] = useState("");
  const [isCustomPricing, setIsCustomPricing] = useState(false);
  const [publicTrialEnabled, setPublicTrialEnabled] = useState(false);
  const [publicTrialDays, setPublicTrialDays] = useState(30);
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);

  const [features, setFeatures] = useState([]);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);

  const normalizeBoolean = (value) => {
    return (
      value === true ||
      value === 1 ||
      value === "1" ||
      String(value || "").trim().toLowerCase() === "true"
    );
  };

  useEffect(() => {
    const getToken = () => localStorage.getItem("token");

    const normalizeFeatureRows = (featureRows) => {
      const rows = Array.isArray(featureRows) ? featureRows : [];

      const coreFeatureIds = rows
        .filter((feature) => Number(feature.is_core) === 1)
        .map((feature) => feature.id);

      return {
        rows,
        coreFeatureIds
      };
    };

    const loadPlan = async () => {
      try {
        setLoading(true);

        const token = getToken();

        const res = await fetch(
          `${API_BASE_URL}/api/subscription-plans/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(
            data.message ||
              "Failed to load subscription plan"
          );
        }

        setPlanKey(data.plan_key || "");
        setPlanName(data.plan_name || "");
        setTierRank(data.tier_rank ?? "");
        setDescription(data.description || "");
        setPricingModel(
          data.pricing_model === "graduated_per_user"
            ? "graduated_per_user"
            : "flat"
        );
        setMinUsers(data.min_users ?? 1);
        setMaxUsers(data.max_users ?? "");
        setMonthlyPrice(data.monthly_price ?? "");
        setAdditionalUserMonthlyPrice(
          data.additional_user_monthly_price ?? ""
        );
        setAnnualPrice(data.annual_price ?? "");
        setAdditionalUserAnnualPrice(
          data.additional_user_annual_price ?? ""
        );
        setIsCustomPricing(
          normalizeBoolean(data.is_custom_pricing)
        );
        setPublicTrialEnabled(
          normalizeBoolean(data.public_trial_enabled)
        );
        setPublicTrialDays(
          data.public_trial_days ?? 30
        );
        setIsActive(
          normalizeBoolean(data.is_active)
        );
        setSortOrder(data.sort_order ?? 0);
      } catch (err) {
        console.error(
          "Load subscription plan error:",
          err.message
        );

        alert(err.message);
      } finally {
        setLoading(false);
      }
    };

    const loadAllFeatures = async () => {
      try {
        setFeaturesLoading(true);

        const token = getToken();

        const res = await fetch(
          `${API_BASE_URL}/api/subscription-plans/features`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(
            data.message ||
              "Failed to load platform features"
          );
        }

        const { rows, coreFeatureIds } =
          normalizeFeatureRows(data);

        setFeatures(rows);
        setSelectedFeatureIds(coreFeatureIds);
      } catch (err) {
        console.error(
          "Load platform features error:",
          err.message
        );

        setFeatures([]);
        setSelectedFeatureIds([]);
      } finally {
        setFeaturesLoading(false);
      }
    };

    const loadPlanFeatures = async () => {
      try {
        setFeaturesLoading(true);

        const token = getToken();

        const res = await fetch(
          `${API_BASE_URL}/api/subscription-plans/${id}/features`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(
            data.message ||
              "Failed to load plan features"
          );
        }

        const { rows, coreFeatureIds } =
          normalizeFeatureRows(data);

        const enabledFeatureIds = rows
          .filter(
            (feature) =>
              Number(feature.enabled_for_plan) === 1
          )
          .map((feature) => feature.id);

        setFeatures(rows);
        setSelectedFeatureIds([
          ...new Set([
            ...coreFeatureIds,
            ...enabledFeatureIds
          ])
        ]);
      } catch (err) {
        console.error(
          "Load plan features error:",
          err.message
        );

        setFeatures([]);
        setSelectedFeatureIds([]);
      } finally {
        setFeaturesLoading(false);
      }
    };

    if (!isNew) {
      loadPlan();
      loadPlanFeatures();
    } else {
      loadAllFeatures();
    }
  }, [id, isNew]);

  const getToken = () =>
    localStorage.getItem("token");

  const isCoreFeature = (feature) =>
    Number(feature.is_core) === 1;

  const toggleFeature = (featureId) => {
    const feature = features.find(
      (item) => item.id === featureId
    );

    if (!feature || isCoreFeature(feature)) {
      return;
    }

    setSelectedFeatureIds((current) =>
      current.includes(featureId)
        ? current.filter(
            (selectedId) =>
              selectedId !== featureId
          )
        : [...current, featureId]
    );
  };

  const savePlanFeatures = async (planId) => {
    const token = getToken();

    const optionalSelectedFeatureIds =
      selectedFeatureIds.filter((featureId) => {
        const feature = features.find(
          (item) => item.id === featureId
        );

        return (
          feature &&
          !isCoreFeature(feature)
        );
      });

    const res = await fetch(
      `${API_BASE_URL}/api/subscription-plans/${planId}/features`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          feature_ids:
            optionalSelectedFeatureIds
        })
      }
    );

    const data = await res.json();

    if (!res.ok) {
      throw new Error(
        data.message ||
          "Failed to save plan features"
      );
    }

    return data;
  };

  const handleSave = async () => {
    try {
      if (
        !planKey.trim() ||
        !planName.trim()
      ) {
        alert(
          "Plan Key and Plan Name are required."
        );

        return;
      }

      if (
        tierRank !== "" &&
        Number(tierRank) <= 0
      ) {
        alert(
          "Tier Rank must be blank or a positive number."
        );

        return;
      }

      const normalizedMinUsers = Number(minUsers);

      if (
        !Number.isInteger(normalizedMinUsers) ||
        normalizedMinUsers < 1
      ) {
        alert(
          "Minimum Users must be a positive whole number."
        );

        return;
      }

      const normalizedMaxUsers =
        maxUsers === ""
          ? null
          : Number(maxUsers);

      if (
        normalizedMaxUsers !== null &&
        (
          !Number.isInteger(normalizedMaxUsers) ||
          normalizedMaxUsers < 1
        )
      ) {
        alert(
          "Maximum Users must be blank or a positive whole number."
        );

        return;
      }

      if (
        normalizedMaxUsers !== null &&
        normalizedMaxUsers < normalizedMinUsers
      ) {
        alert(
          "Maximum Users must be greater than or equal to Minimum Users."
        );

        return;
      }

      const normalizedMonthlyPrice =
        monthlyPrice === ""
          ? null
          : Number(monthlyPrice);

      const normalizedAdditionalUserMonthlyPrice =
        additionalUserMonthlyPrice === ""
          ? null
          : Number(additionalUserMonthlyPrice);

      const normalizedAnnualPrice =
        annualPrice === ""
          ? null
          : Number(annualPrice);

      const normalizedAdditionalUserAnnualPrice =
        additionalUserAnnualPrice === ""
          ? null
          : Number(additionalUserAnnualPrice);

      if (
        pricingModel === "graduated_per_user" &&
        !isCustomPricing
      ) {
        const hasMonthlyPrice =
          normalizedMonthlyPrice !== null &&
          normalizedMonthlyPrice > 0;

        const hasAdditionalMonthlyPrice =
          normalizedAdditionalUserMonthlyPrice !== null &&
          normalizedAdditionalUserMonthlyPrice > 0;

        const hasAnnualPrice =
          normalizedAnnualPrice !== null &&
          normalizedAnnualPrice > 0;

        const hasAdditionalAnnualPrice =
          normalizedAdditionalUserAnnualPrice !== null &&
          normalizedAdditionalUserAnnualPrice > 0;

        if (
          hasMonthlyPrice !==
          hasAdditionalMonthlyPrice
        ) {
          alert(
            "Graduated monthly pricing requires both first-user and additional-user prices."
          );

          return;
        }

        if (
          hasAnnualPrice !==
          hasAdditionalAnnualPrice
        ) {
          alert(
            "Graduated annual pricing requires both first-user and additional-user prices."
          );

          return;
        }

        if (
          !hasMonthlyPrice &&
          !hasAnnualPrice
        ) {
          alert(
            "A graduated per-user plan requires monthly or annual pricing."
          );

          return;
        }
      }

      const normalizedTrialDays =
        Number(publicTrialDays);

      if (
        publicTrialEnabled &&
        (
          !Number.isInteger(
            normalizedTrialDays
          ) ||
          normalizedTrialDays < 1 ||
          normalizedTrialDays > 730
        )
      ) {
        alert(
          "Public Trial Days must be between 1 and 730."
        );

        return;
      }

      setSaving(true);

      const token = getToken();

      const payload = {
        plan_key: planKey.trim(),
        plan_name: planName.trim(),
        tier_rank:
          tierRank === ""
            ? null
            : Number(tierRank),
        description:
          description.trim() || null,
        pricing_model: pricingModel,
        min_users: normalizedMinUsers,
        max_users: normalizedMaxUsers,
        monthly_price:
          normalizedMonthlyPrice,
        additional_user_monthly_price:
          pricingModel === "graduated_per_user"
            ? normalizedAdditionalUserMonthlyPrice
            : null,
        annual_price:
          normalizedAnnualPrice,
        additional_user_annual_price:
          pricingModel === "graduated_per_user"
            ? normalizedAdditionalUserAnnualPrice
            : null,
        is_custom_pricing:
          isCustomPricing,
        public_trial_enabled:
          publicTrialEnabled,
        public_trial_days:
          normalizedTrialDays || 30,
        is_active: isActive,
        sort_order:
          sortOrder === ""
            ? 0
            : Number(sortOrder)
      };

      const res = await fetch(
        isNew
          ? `${API_BASE_URL}/api/subscription-plans`
          : `${API_BASE_URL}/api/subscription-plans/${id}`,
        {
          method: isNew ? "POST" : "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
            "Failed to save subscription plan"
        );
      }

      const savedPlanId =
        isNew ? data.id : id;

      if (!savedPlanId) {
        throw new Error(
          "Plan saved, but no plan ID was returned."
        );
      }

      await savePlanFeatures(savedPlanId);

      navigate(
        "/settings/subscription-plans"
      );
    } catch (err) {
      console.error(
        "Save subscription plan error:",
        err.message
      );

      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const isFeatureGloballyEnabled = (
    feature
  ) => {
    const enabledValue =
      feature.is_enabled !== undefined
        ? feature.is_enabled
        : feature.globally_enabled;

    return (
      Number(enabledValue) === 1 ||
      enabledValue === true
    );
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Topbar />

        <div className="main-content sales-page">
          <div className="sales-section-card">
            Loading subscription plan...
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">
              Agent Book CRM
            </p>

            <h1 className="sales-title">
              {isNew
                ? "New Subscription Plan"
                : planName ||
                  "Edit Subscription Plan"}
            </h1>

            <p className="sales-subtitle">
              Configure plan naming, pricing,
              user limits, upgrade tier,
              trial availability, and included
              features.
            </p>
          </div>

          <div className="sales-header-actions">
            <button
              className="sales-secondary-btn"
              onClick={() =>
                navigate(
                  "/settings/subscription-plans"
                )
              }
              disabled={saving}
            >
              Back to Plans
            </button>

            <button
              className="sales-primary-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? "Saving..."
                : isNew
                  ? "Create Plan"
                  : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="sales-form-grid">
          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Plan Details</h2>

              <p>
                Basic plan identity, upgrade
                ranking, and commercial
                information.
              </p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field">
                <label>Plan Key</label>

                <input
                  value={planKey}
                  onChange={(e) =>
                    setPlanKey(e.target.value)
                  }
                  placeholder="agency"
                />
              </div>

              <div className="sales-field">
                <label>Plan Name</label>

                <input
                  value={planName}
                  onChange={(e) =>
                    setPlanName(e.target.value)
                  }
                  placeholder="Agency Plan"
                />
              </div>

              <div className="sales-field">
                <label>Tier Rank</label>

                <input
                  type="number"
                  min="1"
                  value={tierRank}
                  onChange={(e) =>
                    setTierRank(e.target.value)
                  }
                  placeholder="1 = highest, 3 = lowest"
                />

                <small
                  style={{
                    color: "#64748b"
                  }}
                >
                  Lower number means higher
                  plan. Subscribers can only
                  upgrade to a lower tier rank.
                </small>
              </div>

              <div className="sales-field">
                <label>Sort Order</label>

                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) =>
                    setSortOrder(
                      e.target.value
                    )
                  }
                />
              </div>

              <div className="sales-field sales-field-full">
                <label>Description</label>

                <input
                  value={description}
                  onChange={(e) =>
                    setDescription(
                      e.target.value
                    )
                  }
                  placeholder="Plan description shown to subscribers"
                />
              </div>

              <div className="sales-field">
                <label>Pricing Model</label>

                <select
                  value={pricingModel}
                  onChange={(e) =>
                    setPricingModel(e.target.value)
                  }
                  disabled={isCustomPricing}
                >
                  <option value="flat">
                    Flat Price
                  </option>

                  <option value="graduated_per_user">
                    Graduated Per User
                  </option>
                </select>
              </div>

              <div className="sales-field">
                <label>Minimum Users</label>

                <input
                  type="number"
                  min="1"
                  step="1"
                  value={minUsers}
                  onChange={(e) =>
                    setMinUsers(e.target.value)
                  }
                  placeholder="1"
                />
              </div>

              <div className="sales-field">
                <label>Maximum Users</label>

                <input
                  type="number"
                  min="1"
                  step="1"
                  value={maxUsers}
                  onChange={(e) =>
                    setMaxUsers(e.target.value)
                  }
                  placeholder="Leave blank for unlimited"
                />
              </div>

              <div className="sales-field">
                <label>
                  {pricingModel === "graduated_per_user"
                    ? "First User Monthly Price"
                    : "Monthly Price"}
                </label>

                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={monthlyPrice}
                  onChange={(e) =>
                    setMonthlyPrice(
                      e.target.value
                    )
                  }
                  placeholder="0.00"
                  disabled={isCustomPricing}
                />
              </div>

              {pricingModel === "graduated_per_user" && (
                <div className="sales-field">
                  <label>
                    Additional User Monthly Price
                  </label>

                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={additionalUserMonthlyPrice}
                    onChange={(e) =>
                      setAdditionalUserMonthlyPrice(
                        e.target.value
                      )
                    }
                    placeholder="0.00"
                    disabled={isCustomPricing}
                  />
                </div>
              )}

              <div className="sales-field">
                <label>
                  {pricingModel === "graduated_per_user"
                    ? "First User Annual Price"
                    : "Annual Price"}
                </label>

                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={annualPrice}
                  onChange={(e) =>
                    setAnnualPrice(
                      e.target.value
                    )
                  }
                  placeholder="0.00"
                  disabled={isCustomPricing}
                />
              </div>

              {pricingModel === "graduated_per_user" && (
                <div className="sales-field">
                  <label>
                    Additional User Annual Price
                  </label>

                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={additionalUserAnnualPrice}
                    onChange={(e) =>
                      setAdditionalUserAnnualPrice(
                        e.target.value
                      )
                    }
                    placeholder="0.00"
                    disabled={isCustomPricing}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Plan Settings</h2>

              <p>
                Control pricing behavior,
                public trial availability, and
                plan visibility.
              </p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field">
                <label>Custom Pricing</label>

                <select
                  value={
                    isCustomPricing
                      ? "yes"
                      : "no"
                  }
                  onChange={(e) =>
                    setIsCustomPricing(
                      e.target.value === "yes"
                    )
                  }
                >
                  <option value="no">
                    No
                  </option>

                  <option value="yes">
                    Yes
                  </option>
                </select>
              </div>

              <div className="sales-field">
                <label>Active</label>

                <select
                  value={
                    isActive ? "yes" : "no"
                  }
                  onChange={(e) =>
                    setIsActive(
                      e.target.value === "yes"
                    )
                  }
                >
                  <option value="yes">
                    Yes
                  </option>

                  <option value="no">
                    No
                  </option>
                </select>
              </div>

              <div className="sales-field">
                <label>
                  Public Trial
                </label>

                <select
                  value={
                    publicTrialEnabled
                      ? "yes"
                      : "no"
                  }
                  onChange={(e) =>
                    setPublicTrialEnabled(
                      e.target.value === "yes"
                    )
                  }
                  disabled={isCustomPricing}
                >
                  <option value="no">
                    Disabled
                  </option>

                  <option value="yes">
                    Enabled
                  </option>
                </select>

                <small
                  style={{
                    color: "#64748b"
                  }}
                >
                  Applies to signups from the
                  public WordPress site.
                </small>
              </div>

              <div className="sales-field">
                <label>
                  Public Trial Days
                </label>

                <input
                  type="number"
                  min="1"
                  max="730"
                  value={publicTrialDays}
                  onChange={(e) =>
                    setPublicTrialDays(
                      e.target.value
                    )
                  }
                  disabled={
                    !publicTrialEnabled ||
                    isCustomPricing
                  }
                  placeholder="30"
                />

                <small
                  style={{
                    color: "#64748b"
                  }}
                >
                  Stripe collects the payment
                  method now and starts billing
                  when the trial ends.
                </small>
              </div>
            </div>
          </div>

          <div
            className="sales-section-card sales-section-subscription"
            style={{
              gridColumn: "1 / -1"
            }}
          >
            <div
              className="sales-section-head"
              style={{
                marginBottom: "12px"
              }}
            >
              <h2>Plan Features</h2>

              <p>
                Select optional features for
                this plan. Core features are
                included automatically.
              </p>
            </div>

            {featuresLoading ? (
              <div className="profile-empty-card">
                Loading features...
              </div>
            ) : features.length === 0 ? (
              <div className="profile-empty-card">
                No platform features found.
              </div>
            ) : (
              <div
                style={{
                  border:
                    "1px solid #e2e8f0",
                  borderRadius: "12px",
                  overflow: "hidden",
                  background: "#ffffff"
                }}
              >
                {features.map(
                  (feature, index) => {
                    const globallyEnabled =
                      isFeatureGloballyEnabled(
                        feature
                      );

                    const core =
                      isCoreFeature(feature);

                    const checked =
                      core ||
                      selectedFeatureIds.includes(
                        feature.id
                      );

                    const disabled =
                      core ||
                      !globallyEnabled;

                    return (
                      <label
                        key={feature.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "34px minmax(170px, 1.1fr) minmax(220px, 2fr) 120px",
                          alignItems:
                            "center",
                          gap: "12px",
                          minHeight:
                            "48px",
                          padding:
                            "8px 12px",
                          borderTop:
                            index === 0
                              ? "none"
                              : "1px solid #edf2f7",
                          background:
                            checked
                              ? "#f8fafc"
                              : "#ffffff",
                          opacity:
                            globallyEnabled
                              ? 1
                              : 0.55,
                          cursor:
                            disabled
                              ? "not-allowed"
                              : "pointer"
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() =>
                            toggleFeature(
                              feature.id
                            )
                          }
                          style={{
                            width: "15px",
                            height: "15px",
                            margin: 0
                          }}
                        />

                        <div
                          style={{
                            minWidth: 0,
                            color:
                              "#0f172a",
                            fontSize:
                              "13px",
                            fontWeight:
                              800,
                            whiteSpace:
                              "nowrap",
                            overflow:
                              "hidden",
                            textOverflow:
                              "ellipsis"
                          }}
                          title={
                            feature.feature_name
                          }
                        >
                          {
                            feature.feature_name
                          }
                        </div>

                        <div
                          style={{
                            minWidth: 0,
                            color:
                              "#64748b",
                            fontSize:
                              "12px",
                            lineHeight:
                              1.35,
                            whiteSpace:
                              "nowrap",
                            overflow:
                              "hidden",
                            textOverflow:
                              "ellipsis"
                          }}
                          title={
                            feature.description ||
                            feature.feature_key
                          }
                        >
                          {feature.description ||
                            feature.feature_key}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent:
                              "flex-end",
                            gap: "6px",
                            alignItems:
                              "center"
                          }}
                        >
                          {core && (
                            <span
                              style={{
                                borderRadius:
                                  "999px",
                                padding:
                                  "3px 8px",
                                background:
                                  "#eef2ff",
                                color:
                                  "#4338ca",
                                fontSize:
                                  "11px",
                                fontWeight:
                                  800,
                                whiteSpace:
                                  "nowrap"
                              }}
                            >
                              Core
                            </span>
                          )}

                          {!core &&
                            checked && (
                              <span
                                style={{
                                  borderRadius:
                                    "999px",
                                  padding:
                                    "3px 8px",
                                  background:
                                    "#ecfdf5",
                                  color:
                                    "#047857",
                                  fontSize:
                                    "11px",
                                  fontWeight:
                                    800,
                                  whiteSpace:
                                    "nowrap"
                                }}
                              >
                                Included
                              </span>
                            )}

                          {!core &&
                            !checked && (
                              <span
                                style={{
                                  borderRadius:
                                    "999px",
                                  padding:
                                    "3px 8px",
                                  background:
                                    "#f1f5f9",
                                  color:
                                    "#64748b",
                                  fontSize:
                                    "11px",
                                  fontWeight:
                                    800,
                                  whiteSpace:
                                    "nowrap"
                                }}
                              >
                                Optional
                              </span>
                            )}
                        </div>
                      </label>
                    );
                  }
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SubscriptionPlanEditor;